import fs from 'node:fs';
import path from 'node:path';
import type { ChildProcess } from 'node:child_process';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { mergeEnvFile } from '../lib/envfile.js';
import {
  detectLocalUrl,
  killTree,
  npmRunDev,
  pythonDev,
  spawnDevServer,
  waitForHttp,
} from '../lib/procs.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, note, type Io } from '../output.js';

interface DevCreds {
  databaseUrl: string;
  role: string;
  schema: string;
  /** Used by the app to verify a secure connection to the Ghosty database. */
  serverCa?: string;
}

/**
 * `ghosty dev` — run the app locally:
 *   1. fetch personal dev DB creds from admin-app (database apps only),
 *   2. merge DATABASE_URL into backend/.env.local (user lines preserved),
 *   3. spawn `npm run dev` in backend/ + frontend/, prefix their output,
 *   4. once both local URLs respond, print one parseable ready line:
 *        READY frontend=<url> backend=<url>
 *   Ctrl-C kills both process trees (taskkill on Windows).
 */
export async function runDev(options: {
  dir?: string;
  json?: boolean;
  adminUrl?: string;
}): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');

  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  let config;
  try {
    config = readAppConfig(dir);
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }

  const backendDir = path.join(dir, 'backend');
  const frontendDir = path.join(dir, 'frontend');
  if (!fs.existsSync(backendDir) || !fs.existsSync(frontendDir)) {
    fail(io, `Expected backend/ and frontend/ folders in ${dir}.`, 1, 'MISSING_FOLDERS');
  }

  const envUpdates: Record<string, string> = { NODE_ENV: 'development' };
  if (config.database === true) {
    note(io, 'Connecting to your Ghosty database…');
    try {
      const client = new ApiClient(resolved.adminUrl, resolved.profile.token);
      const creds = await client.get<DevCreds>(
        `/builder-api/me/dev-creds?slug=${encodeURIComponent(config.slug)}`,
      );
      envUpdates.DATABASE_URL = creds.databaseUrl;
      // The app uses this to verify a secure connection to the Ghosty database.
      if (creds.serverCa) envUpdates.DB_SERVER_CA = creds.serverCa;
      note(io, '  Connected to your Ghosty database');
    } catch (err) {
      fail(io, `Couldn't connect to your Ghosty database: ${(err as Error).message}`, 1, 'DEV_CREDS_FAILED');
    }
  }
  mergeEnvFile(path.join(backendDir, '.env.local'), envUpdates);

  note(io, 'Starting dev servers (Ctrl-C stops both)...');
  // Node backends run `npm run dev`; python backends run the FastAPI
  // entrypoint directly (templates contract: src/main.py reads PORT).
  const backendCommand =
    config.backend === 'python' && !fs.existsSync(path.join(backendDir, 'package.json'))
      ? pythonDev()
      : npmRunDev();
  const children: Array<{
    name: string;
    proc: ChildProcess;
    url: string | null;
  }> = [
    { name: 'backend', proc: spawnDevServer(backendCommand, backendDir), url: null },
    { name: 'frontend', proc: spawnDevServer(npmRunDev(), frontendDir), url: null },
  ];

  let announced = false;
  const announceIfReady = async (): Promise<void> => {
    if (announced || !children.every((c) => c.url)) return;
    announced = true;
    const backendUrl = children[0].url!;
    const frontendUrl = children[1].url!;
    // "Ports respond" gate: a printed URL is necessary but not sufficient.
    await Promise.all([waitForHttp(frontendUrl, 60_000), waitForHttp(backendUrl, 60_000)]);
    if (io.json) {
      emitJson({ ok: true, event: 'ready', frontend: frontendUrl, backend: backendUrl });
    } else {
      process.stdout.write(
        `${pc.green('READY')} frontend=${frontendUrl} backend=${backendUrl}\n`,
      );
    }
  };

  for (const child of children) {
    const prefix = pc.dim(`[${child.name}]`);
    const onChunk = (chunk: Buffer | string): void => {
      for (const line of chunk.toString().split(/\r?\n/)) {
        if (line.length === 0) continue;
        if (!io.json) process.stderr.write(`${prefix} ${line}\n`);
        if (!child.url) {
          const url = detectLocalUrl(line);
          if (url) {
            child.url = url;
            void announceIfReady();
          }
        }
      }
    };
    child.proc.stdout?.on('data', onChunk);
    child.proc.stderr?.on('data', onChunk);
    child.proc.on('error', (err) => {
      fail(io, `Failed to start ${child.name} dev server: ${err.message}`, 1, 'SPAWN_FAILED');
    });
  }

  const shutdown = (): void => {
    note(io, 'Shutting down dev servers...');
    for (const child of children) killTree(child.proc);
  };
  process.once('SIGINT', shutdown);
  process.once('SIGTERM', shutdown);

  await Promise.all(
    children.map(
      (c) =>
        new Promise<void>((resolve) => {
          c.proc.once('exit', () => resolve());
        }),
    ),
  );
}
